import { PropsWithChildren, useMemo } from 'react';
import { BoostIconDisplay } from 'src/components/SponsoredListings/BoostIconDisplay';
import {
  CatalogDataContextV2,
  CatalogDataContextWithSelector,
} from 'src/contexts/CatalogDataContext/CatalogDataContext.types';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext/useCatalogDataContext';
import { PosAnimatingProgressBar } from 'src/core/POS/PosProgressBar/PosAnimatingProgressBar';
import { vars } from 'src/core/themes';
import { formatCurrency } from 'src/utils/numberFormatter';
import {
  Performer,
  SponsoredListingsCampaign,
  Venue,
} from 'src/WebApiController';

import * as styles from './BoostEventSearchModal.css';

/**
 * Renders the active campaign's spend state — the boost bar, the remaining
 * budget value, and the boost icon — mirroring BoostListingButton's display
 * so an already-boosted event surfaces its campaign directly in the search
 * result card.
 */
export const ActiveBoostDisplay = ({
  campaign,
}: {
  campaign: SponsoredListingsCampaign;
}) => {
  const budget = campaign.activeFlight?.flightBudget ?? 0;
  const spend = campaign.activeFlight?.flightSpend ?? 0;
  const remaining = budget - spend;

  return (
    <div className={styles.activeBoostDisplayContainer}>
      <PosAnimatingProgressBar
        style={{
          width: '80px',
          backgroundColor: vars.color.surfaceSecondary,
        }}
        enableGlow
        value={(remaining / (budget || 1)) * 100}
      />
      <span className={styles.activeBoostDisplayValue}>
        {formatCurrency(remaining, campaign.currencyCode ?? 'USD')}
      </span>
      <BoostIconDisplay
        size={vars.iconSize.m}
        spendStrategy={campaign.spendStrategy}
        targetRoas={campaign.targetRoas}
        biddingMode={campaign.biddingMode}
      />
    </div>
  );
};

/**
 * Wraps the boost event search with a catalog context whose data includes
 * performer/venue records fetched for the scoped events (see
 * useBoostIngressEntityCatalog). This mirrors the page-level
 * CampaignCatalogAugmentationProvider pattern so the shared EventSearch,
 * which derives scoped performer/venue results from the ambient catalog,
 * can resolve them on the Sponsored Listings page.
 */
export const BoostEventSearchCatalogProvider = ({
  performers,
  venues,
  children,
}: PropsWithChildren<{
  performers?: Record<string, Performer>;
  venues?: Record<string, Venue>;
}>) => {
  const baseContext = useCatalogDataContext();

  const augmentedContext = useMemo(
    () => ({
      ...baseContext,
      data: baseContext.data
        ? {
            ...baseContext.data,
            performers: {
              ...baseContext.data.performers,
              ...performers,
            },
            venues: {
              ...baseContext.data.venues,
              ...venues,
            },
          }
        : baseContext.data,
    }),
    [baseContext, performers, venues]
  );

  return (
    <CatalogDataContextV2.Provider value={augmentedContext}>
      <CatalogDataContextWithSelector.Provider value={augmentedContext}>
        {children}
      </CatalogDataContextWithSelector.Provider>
    </CatalogDataContextV2.Provider>
  );
};
